import { LangIcon } from '@/assets/theme/default/general/LangIcon';
import { ConfirmDialog } from '@/components/blocks/ConfirmDialog/ConfirmDialog';
import { Box, Typography } from '@mui/material';
import { usePhrase } from '@/hooks';
import { HEADER_PHRASES } from '@/consts/phrases/headerPhrases';
import { useModalStore } from '@/store/modalStore';
import { useRouter } from '@/hooks';
import { usePhrasesStore } from '@/store';
import { shallow } from 'zustand/shallow';
import { GENERAL_PHRASES } from '@/consts/phrases/generalPhrases';
import { getLocale, updateLocale } from '@/utils';
import { useCurrentPath } from '@/hooks/general/useCurrentPath/useCurrentPath';
import { useLocale } from '@/hooks/general/useLocale/useLocale';
import * as Sentry from '@sentry/nextjs';

export const LangSwitchConfirm = () => {
    const { t } = usePhrase();
    const router = useRouter();
    const currentPath = useCurrentPath();
    const i18nLocale = useLocale();

    const { modals, updateModal } = useModalStore(
        (s) => ({
            modals: s.modals,
            updateModal: s.updateModal,
        }),
        shallow,
    );
    const { setLocale, setLocaleHasBeenChecked } = usePhrasesStore();
    const { localeConfirm } = modals;
    const { selectedLocale, isLoggedIn } = localeConfirm.details;

    const handleClose = () => {
        setLocaleHasBeenChecked();
        updateModal('localeConfirm', {
            isOpen: false,
            details: { selectedLocale: undefined, isLoggedIn: false },
        });
    };

    const handleConfirm = async () => {
        try {
            await updateLocale({
                locale: selectedLocale,
                isLoggedIn: isLoggedIn,
                router,
                currentPath,
                i18nLocale,
                setLocale,
            });
        } catch (err: unknown) {
            Sentry.captureException(err);
        } finally {
            handleClose();
        }
    };
    return (
        <ConfirmDialog
            title={
                localeConfirm.details.selectedLocale && i18nLocale.locale ? (
                    <Title
                        selectedLocaleString={localeConfirm.details.selectedLocale}
                        currentLocaleString={i18nLocale.locale}
                    />
                ) : (
                    ''
                )
            }
            data-testid="confirm-lang-switch"
            open={localeConfirm.isOpen}
            actionHandler={handleConfirm}
            handleClose={handleClose}
            hideCloseButton={true}
            actionText={t(GENERAL_PHRASES.YES)}
            cancelText={t(GENERAL_PHRASES.NO)}
        />
    );
};

const Title = (props: { selectedLocaleString: string; currentLocaleString: string }) => {
    const { t } = usePhrase();
    const selectedLocale = getLocale(props.selectedLocaleString);
    const currentLocale = getLocale(props.currentLocaleString);
    const currentLanguage = `${currentLocale.country.localeName} (${currentLocale.language.localeName})`;
    const selectedLanguage = `${selectedLocale.country.localeName} (${selectedLocale.language.localeName})`;

    return (
        <Box sx={{ display: 'flex', flexDirection: ['column', 'row'], alignItems: ['center'] }}>
            <LangIcon />
            <Typography variant="body1" sx={{ width: ['initial', 450], ml: [0, 2], mt: [2, 0] }}>
                {t(HEADER_PHRASES.LANGUAGE_SWITCH_CONFIRM, {
                    replaceTextVars: [currentLanguage, selectedLanguage],
                })}
            </Typography>
        </Box>
    );
};
